import React, { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, Form, Row, Col } from 'react-bootstrap';
import Button from 'components/base/Button';
import { createIntegration, updateIntegration } from '../../../../redux/slices/lists/integrationsSlice';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { getFolders } from '../../../../redux/slices/lists/foldersSlice';
import { getConnections } from '../../../../redux/slices/lists/connectionsSlice';
import api from '../../../../services/api';

const EditIntegration = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { integrationId } = useParams<{ integrationId: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!integrationId;

  const { zones } = useAppSelector((state) => state.listOfZones);
  const { folders } = useAppSelector((state) => state.listOfFolders);
  const { loading } = useAppSelector((state) => state.listOfIntegrations);
  const { connections = [] } = useAppSelector((state) => state?.listOfConnections);

  const [form, setForm] = useState({
    name: '',
    description: '',
    zone_id: '',
    parent_folder_id: '',
    base_url: '',
    connection_id: '',
    auth_basic: false,
    auth_bearer: false,
    auth_oauth2: false,
  });
  const [loadingIntegration, setLoadingIntegration] = useState(false);

  // Create pre-fill from the Workspace: ?zoneId & ?folderId seed the new
  // integration's zone / parent folder.
  useEffect(() => {
    if (isEditMode) return;
    const zoneId = searchParams.get('zoneId');
    const folderId = searchParams.get('folderId');
    if (zoneId || folderId) {
      setForm((prev) => ({ ...prev, zone_id: zoneId ?? prev.zone_id, parent_folder_id: folderId ?? prev.parent_folder_id }));
    }
  }, [isEditMode, searchParams]);

  // Fetch zones, folders, and REST connections for dropdowns
  useEffect(() => {
    const action = isEditMode ? 'update' : 'create';
    dispatch(getAuthorizedZones({ resourceType: 'integrations', action }) as any);
    dispatch(getFolders() as any);
    dispatch(getConnections('rest') as any);
  }, [dispatch, isEditMode]);

  // Fetch existing integration data in edit mode
  useEffect(() => {
    if (isEditMode && integrationId) {
      setLoadingIntegration(true);
      api
        .get(`/integrations/${integrationId}`)
        .then((res) => {
          const data = res.data;
          setForm({
            name: data.name || '',
            description: data.description || '',
            zone_id: data.zone_id ? String(data.zone_id) : '',
            parent_folder_id: '',
            base_url: data.base_url || '',
            connection_id: data.connection_id ? String(data.connection_id) : '',
            auth_basic: data.auth_basic || false,
            auth_bearer: data.auth_bearer || false,
            auth_oauth2: data.auth_oauth2 || false,
          });
        })
        .catch((err) => {
          console.error('Error fetching integration:', err);
        })
        .finally(() => setLoadingIntegration(false));
    }
  }, [isEditMode, integrationId]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      if (name === 'zone_id') {
        return { ...prev, zone_id: value, parent_folder_id: '' };
      }
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
    });
  };

  // Filter folders by selected zone
  const filteredFolders = useMemo(() => {
    if (!form.zone_id || !folders) return [];
    return folders.filter((f: any) => f.zone_id === Number(form.zone_id));
  }, [folders, form.zone_id]);

  const buildPayload = () => {
    const payload: any = {
      name: form.name,
      description: form.description,
      zone_id: form.zone_id ? Number(form.zone_id) : undefined,
      base_url: form.base_url,
      connection_id: form.connection_id ? Number(form.connection_id) : null,
      auth_basic: form.auth_basic,
      auth_bearer: form.auth_bearer,
      auth_oauth2: form.auth_oauth2,
    };
    if (!isEditMode) {
      payload.parent_folder_id = form.parent_folder_id ? Number(form.parent_folder_id) : null;
    }
    return payload;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (isEditMode) {
      await dispatch(
        updateIntegration({
          ...payload,
          integration_id: Number(integrationId),
        }) as any
      );
    } else {
      const result = await dispatch(createIntegration(payload) as any);
      if (!result.error && result.payload?.integration_id) {
        navigate(`/configuration/integrations/${result.payload.integration_id}/edit`, { replace: true });
      }
    }
  };

  const handleSaveAndClose = async () => {
    const payload = buildPayload();
    let result;
    if (isEditMode) {
      result = await dispatch(
        updateIntegration({
          ...payload,
          integration_id: Number(integrationId),
        }) as any
      );
    } else {
      result = await dispatch(createIntegration(payload) as any);
    }
    if (!result.error) {
      navigate('/configuration/integrations');
    }
  };

  const handleExit = () => {
    navigate('/configuration/integrations');
  };

  const isSaving = loading.createIntegration || loading.updateIntegration;

  if (loadingIntegration) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-4">
        <h3>{isEditMode ? 'Edit Integration' : 'Create New Integration'}</h3>
        <p className="text-muted mb-0">Configuration &gt; Integration Wizard &gt; {isEditMode ? 'Edit' : 'Create New'}</p>
      </div>

      {/* Basic Properties */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Basic Properties</h5>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" placeholder="e.g., Jira Connector" name="name" value={form.name} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe the integration..."
                name="description"
                value={form.description}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Zone</Form.Label>
              <Form.Select name="zone_id" value={form.zone_id} onChange={handleChange} required>
                <option value="">Select a zone</option>
                {(zones || []).map((zone: any) => (
                  <option key={zone.zone_id} value={zone.zone_id}>
                    {zone.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {!isEditMode && (
              <Form.Group className="mb-3">
                <Form.Label>Folder</Form.Label>
                <Form.Select name="parent_folder_id" value={form.parent_folder_id} onChange={handleChange}>
                  <option value="">None (root)</option>
                  {filteredFolders.map((folder: any) => (
                    <option key={folder.folder_id} value={folder.folder_id}>
                      {folder.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">A child folder will be created automatically for this integration.</Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Base URL</Form.Label>
              <Form.Control type="text" placeholder="https://api.example.com" name="base_url" value={form.base_url} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Connection</Form.Label>
              <Form.Select name="connection_id" value={form.connection_id} onChange={handleChange}>
                <option value="">Auto-create</option>
                {connections.map((conn: any) => (
                  <option key={conn.connection_id} value={conn.connection_id}>
                    {conn.connection_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select an existing REST connection or leave as &quot;Auto-create&quot; to generate one automatically from the Base URL.
              </Form.Text>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      {/* Authentication */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-2">Authentication</h5>
          <p className="text-muted mb-3">
            Select default authentication types for the nodes in this Integration. Any selections made here will be the default value(s) for all nodes created
            in the Integration, but they can be overridden as needed.
          </p>
          <Row>
            <Col xs="auto">
              <Form.Check type="checkbox" label="Basic" name="auth_basic" checked={form.auth_basic} onChange={handleChange} />
            </Col>
            <Col xs="auto">
              <Form.Check type="checkbox" label="Bearer" name="auth_bearer" checked={form.auth_bearer} onChange={handleChange} />
            </Col>
            <Col xs="auto">
              <Form.Check type="checkbox" label="OAuth 2.0" name="auth_oauth2" checked={form.auth_oauth2} onChange={handleChange} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Bottom Buttons */}
      <div className="d-flex justify-content-end gap-2 mb-4">
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !form.name || !form.zone_id}>
          Save
        </Button>
        <Button variant="primary" onClick={handleSaveAndClose} disabled={isSaving || !form.name || !form.zone_id}>
          Save and Close
        </Button>
        <Button variant="danger" onClick={handleExit}>
          Exit
        </Button>
      </div>
    </div>
  );
};

export default EditIntegration;
